import { produce } from 'immer';
import { createCreateAllInsanityOutbound, createCreateMttOutbound, createCreateRegularOutbound, createCreateSttOutbound } from '../../api/outbound/lobbyOutbounds';
import { isConfigFlagEnabled } from '../../config';
import { ALL_BUY_IN_AMOUNTS } from '../../constants/buyInConstants';
import { DUAL_LIMIT_AUTO_ENABLED_BUY_IN_THRESHOLD } from '../../constants/constants';
import { getBuyInRangeAmounts } from '../../utils/buyInRangeUtils';
import { getDefaultNumSeats } from '../../utils/numSeatsUtils';
import { isLocalhostEnvironment, isTestEnvironment } from '../../utils/utils';
import { CLEAR_CREATE_GAME_DIALOG, SET_BLINDS_INDEX, SET_BUY_IN_INDEX, SET_BUY_IN_RANGE, SET_CURRENCY_INDEX, SET_GAME_SPEED_INDEX, SET_IS_AGGRO_BETTING_FILTER_ENABLED, SET_IS_DUAL_LIMIT_ENABLED, SET_IS_POWER_PLAYERS_ONLY, SET_IS_SIDE_BETS_PHASE_ENABLED, SET_NUM_MTT_SEATS, SET_NUM_SEATS, SET_ROOM_NAME, SET_ROOM_TYPE, SET_VARIANT_ID } from '../actions/createGameActions';
const INITIAL_ROOM_TYPE = 'regular';
const INITIAL_ROOM_NAME = '';
const INITIAL_BUY_IN_INDEX = isLocalhostEnvironment() && !isTestEnvironment() ? 1 : 0;
const INITIAL_BUY_IN_RANGE = isLocalhostEnvironment() ? 'none' : 'wide'; // default to 'wide' on live/prod server
const INITIAL_GAME_SPEED_INDEX = 1; // normal speed - couldn't determine this at runtime with getGameSpeedIndex(), possible circular reference?
const INITIAL_BLINDS_INDEX = 1; // normal
const INITIAL_VARIANT_ID = 0; // texas hold 'em
const INITIAL_CURRENCY_INDEX = 0; // chips
const INITIAL_NUM_SEATS = getDefaultNumSeats();
const INITIAL_NUM_MTT_SEATS = isLocalhostEnvironment() ? 2 : 5;
const INITIAL_AGGRO_BETTING_FILTER_ENABLED = false;
const INITIAL_DUAL_LIMIT_ENABLED = !isLocalhostEnvironment();
const INITIAL_SIDE_BETS_PHASE_ENABLED = false;
const INITIAL_IS_POWER_PLAYERS_ONLY = false;
const initialState = {
    roomType: INITIAL_ROOM_TYPE,
    roomName: INITIAL_ROOM_NAME,
    gameSpeedIndex: INITIAL_GAME_SPEED_INDEX,
    buyInIndex: INITIAL_BUY_IN_INDEX,
    buyInRange: INITIAL_BUY_IN_RANGE,
    blindsIndex: INITIAL_BLINDS_INDEX,
    variantId: INITIAL_VARIANT_ID,
    currencyIndex: INITIAL_CURRENCY_INDEX,
    numSeats: INITIAL_NUM_SEATS,
    numMttSeats: INITIAL_NUM_MTT_SEATS,
    isAggroBettingFilterEnabled: INITIAL_AGGRO_BETTING_FILTER_ENABLED,
    isDualLimitEnabled: INITIAL_DUAL_LIMIT_ENABLED,
    isSideBetsPhaseEnabled: INITIAL_SIDE_BETS_PHASE_ENABLED,
    isPowerPlayersOnly: INITIAL_IS_POWER_PLAYERS_ONLY,
    outbound: createCreateRegularOutbound({
        roomName: INITIAL_ROOM_NAME,
        buyInIndex: INITIAL_BUY_IN_INDEX,
        buyInRange: INITIAL_BUY_IN_RANGE,
        gameSpeedIndex: INITIAL_GAME_SPEED_INDEX,
        blindsIndex: INITIAL_BLINDS_INDEX,
        variantId: INITIAL_VARIANT_ID,
        currencyIndex: INITIAL_CURRENCY_INDEX,
        numSeats: INITIAL_NUM_SEATS,
        isAggroBettingFilterEnabled: INITIAL_AGGRO_BETTING_FILTER_ENABLED,
        isDualLimitEnabled: INITIAL_DUAL_LIMIT_ENABLED,
        isSideBetsPhaseEnabled: INITIAL_SIDE_BETS_PHASE_ENABLED,
        isPowerPlayersOnly: INITIAL_IS_POWER_PLAYERS_ONLY
    })
};
function createOutbound(state) {
    const { roomName, buyInIndex, buyInRange, gameSpeedIndex, blindsIndex, variantId, currencyIndex, numSeats, numMttSeats, isAggroBettingFilterEnabled, isDualLimitEnabled, isSideBetsPhaseEnabled, isPowerPlayersOnly } = state;
    switch (state.roomType) {
        case 'regular': {
            return createCreateRegularOutbound({
                roomName,
                buyInIndex,
                buyInRange,
                gameSpeedIndex,
                blindsIndex,
                variantId,
                currencyIndex,
                numSeats,
                isAggroBettingFilterEnabled,
                isDualLimitEnabled,
                isSideBetsPhaseEnabled,
                isPowerPlayersOnly
            });
        }
        case 'stt': {
            return createCreateSttOutbound({
                roomName,
                buyInIndex,
                gameSpeedIndex,
                variantId,
                currencyIndex,
                numSeats,
                isPowerPlayersOnly
            });
        }
        case 'mtt': {
            return createCreateMttOutbound({
                roomName,
                buyInIndex,
                gameSpeedIndex,
                variantId,
                currencyIndex,
                numMttSeats,
                isPowerPlayersOnly
            });
        }
        case 'all-insanity': {
            return createCreateAllInsanityOutbound({
                roomName,
                buyInIndex,
                buyInRange,
                gameSpeedIndex,
                currencyIndex,
                numSeats
            });
        }
    }
}
const createRoomReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            case CLEAR_CREATE_GAME_DIALOG: {
                return initialState;
            }
            case SET_ROOM_TYPE: {
                draft.roomType = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_ROOM_NAME: {
                draft.roomName = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_GAME_SPEED_INDEX: {
                draft.gameSpeedIndex = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_BUY_IN_INDEX: {
                draft.buyInIndex = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_BUY_IN_RANGE: {
                draft.buyInRange = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_BLINDS_INDEX: {
                draft.blindsIndex = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_VARIANT_ID: {
                draft.variantId = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_CURRENCY_INDEX: {
                draft.currencyIndex = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_NUM_SEATS: {
                draft.numSeats = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_NUM_MTT_SEATS: {
                draft.numMttSeats = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_IS_AGGRO_BETTING_FILTER_ENABLED: {
                draft.isAggroBettingFilterEnabled = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_IS_DUAL_LIMIT_ENABLED: {
                draft.isDualLimitEnabled = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_IS_SIDE_BETS_PHASE_ENABLED: {
                draft.isSideBetsPhaseEnabled = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            case SET_IS_POWER_PLAYERS_ONLY: {
                draft.isPowerPlayersOnly = action.payload;
                draft.outbound = createOutbound(draft);
                break;
            }
            default: {
                return state;
            }
        }
    });
};
export function selectIsDualLimitAutoEnabled(state) {
    if (isTestEnvironment()) {
        return false;
    }
    if (!isConfigFlagEnabled('dualLimitGameSetting')) {
        return false;
    }
    const buyIn = ALL_BUY_IN_AMOUNTS[state.createGame.buyInIndex];
    const [minBuyIn] = getBuyInRangeAmounts(buyIn, state.createGame.buyInRange);
    return minBuyIn <= DUAL_LIMIT_AUTO_ENABLED_BUY_IN_THRESHOLD;
}
export default createRoomReducer;
